'use client'

import type { ReactNode } from 'react'
import type { ProcessedTheme } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { CampField } from '../../../ProcessedEntry/DomainEntry/sharedComponents'
import { parseDbTimestamp } from '@/lib/dates'

export type CommType = 'sms' | 'email'

function toDatetimeLocal(iso: string): string {
  const d = parseDbTimestamp(iso)
  if (!d) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export interface CampaignEditFieldsProps {
  theme: ProcessedTheme
  // Headline
  editHeadline: string
  setEditHeadline: (v: string) => void
  headlineAction?: ReactNode
  // Date
  createdAt: string
  setCreatedAt: (v: string) => void
  // Description
  editDescription: string
  setEditDescription: (v: string) => void
  // Communication
  commType: CommType
  setCommType: (v: CommType) => void
  commSender: string
  setCommSender: (v: string) => void
  commSubject: string
  setCommSubject: (v: string) => void
  commBody: string
  setCommBody: (v: string) => void
}

export function CampaignEditFields({
  theme,
  editHeadline,
  setEditHeadline,
  headlineAction,
  createdAt,
  setCreatedAt,
  editDescription,
  setEditDescription,
  commType,
  setCommType,
  commSender,
  setCommSender,
  commSubject,
  setCommSubject,
  commBody,
  setCommBody,
}: CampaignEditFieldsProps) {
  const isDark = theme === 'dark'
  const c = watchClasses(theme)
  const border = 'border-border'
  const muted  = 'text-muted-foreground'
  const cardBg = 'bg-card'

  return (
    <div className="space-y-4">
      {/* Headline */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Headline</span>
          {headlineAction}
        </div>
        <input
          value={editHeadline}
          onChange={(e) => setEditHeadline(e.target.value)}
          className={`w-full h-8 border rounded-md px-3 text-[13px] focus:outline-none focus:ring-1 focus:ring-ring ${c.input}`}
          placeholder="Public-facing headline…"
        />
      </div>

      {/* Date */}
      <CampField label="Date">
        <input
          type="datetime-local"
          value={toDatetimeLocal(createdAt)}
          onChange={(e) => {
            if (!e.target.value) return
            setCreatedAt(new Date(e.target.value).toISOString())
          }}
          className={`h-8 border rounded-md px-3 text-[13px] focus:outline-none focus:ring-1 focus:ring-ring ${c.input}`}
        />
      </CampField>

      {/* Description */}
      <CampField label="Description">
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          rows={6}
          className={`w-full border rounded-md px-3 py-2 text-[13px] font-sans resize-y focus:outline-none focus:ring-1 focus:ring-ring ${c.input}`}
          placeholder="Public-facing description…"
        />
      </CampField>

      {/* Communication preview data */}
      <div className={`border p-4 space-y-3 ${border}`}>
        <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>
          Communication preview data
        </div>

        {/* Type + sender */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1 shrink-0">
            {(['sms', 'email'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setCommType(t)}
                className={`font-mono text-[9px] uppercase tracking-widest border px-2.5 py-1 transition-colors ${
                  commType === t
                    ? 'bg-primary text-primary-foreground border-signal/30 text-white'
                    : `${border} ${muted} hover:border-signal hover:text-signal`
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className={`font-mono text-[9px] uppercase tracking-widest shrink-0 ${muted}`}>Sender</span>
            <input
              value={commSender}
              onChange={(e) => setCommSender(e.target.value)}
              className={`flex-1 min-w-0 h-7 border rounded px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-ring ${c.input}`}
              placeholder="e.g. CTT, noreply@banco.pt…"
            />
          </div>
        </div>

        {/* Subject (email only) */}
        {commType === 'email' && (
          <div className="flex items-center gap-2">
            <span className={`font-mono text-[9px] uppercase tracking-widest shrink-0 w-14 ${muted}`}>Subject</span>
            <input
              value={commSubject}
              onChange={(e) => setCommSubject(e.target.value)}
              className={`flex-1 h-7 border rounded px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-ring ${c.input}`}
              placeholder="Email subject…"
            />
          </div>
        )}

        {/* Body */}
        <div className="flex gap-2">
          <span className={`font-mono text-[9px] uppercase tracking-widest shrink-0 w-14 pt-1.5 ${muted}`}>
            {commType === 'email' ? 'Body' : 'Message'}
          </span>
          <textarea
            value={commBody}
            onChange={(e) => setCommBody(e.target.value)}
            rows={4}
            className={`flex-1 border rounded px-2 py-1.5 text-[12px] font-sans resize-y focus:outline-none focus:ring-1 focus:ring-ring ${c.input}`}
            placeholder="Message content…"
          />
        </div>
      </div>
    </div>
  )
}
